import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { subscribeWithTransientRetry, type DocSnap } from '../lib/support/listenerRetry';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { loadUserOrganizations, selectOrganization } from '../lib/organizationLoad';
import { useAuth } from './AuthContext';
import { isConnectivityFailure, loadSavedIdentity, saveIdentity } from '../lib/auth/offlineIdentityCache';
import type { Organization, OrganizationMember, MemberRole } from '../types/firestore';


// ============================================================================
// TYPES
// ============================================================================

interface OrganizationContextType {
  // Current organization
  currentOrganization: Organization | null;
  currentMembership: OrganizationMember | null;

  // User's organizations
  organizations: Organization[];
  memberships: OrganizationMember[];

  // Loading state
  loading: boolean;

  // Set when the last organizations/memberships fetch failed — distinguishes
  // "genuinely has no orgs" from "failed to load orgs" for downstream consumers.
  error: Error | null;

  // True while `currentOrganization` is the org SAVED ON THIS DEVICE because the
  // live load failed for lack of signal (lib/auth/offlineIdentityCache.ts). The
  // load stays in its error state so the reconnect/foreground retry still runs.
  organizationFromDevice: boolean;

  // Methods
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;

  // Re-run the initial membership load after a failure. Synchronous and
  // idempotent — safe to wire straight to a Retry button.
  retryLoad: () => void;

  // Utility methods
  hasOrgRole: (role: MemberRole) => boolean;
  isOrgOwner: () => boolean;
  isOrgAdmin: () => boolean;
  canManageMembers: () => boolean;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user, firebaseUser } = useAuth();

  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentMembership, setCurrentMembership] = useState<OrganizationMember | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [memberships, setMemberships] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [organizationFromDevice, setOrganizationFromDevice] = useState(false);
  // Bumped to re-run the load effect. A failed load is not terminal: reconnect
  // and foreground both retry, because the alternative is what shipped — one
  // dead-zone startup leaving `currentOrganization` null for the life of the
  // JS context, which on an installed PWA means until the tech force-quits.
  const [reloadNonce, setReloadNonce] = useState(0);

  const retryLoad = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Fetch user's organizations and memberships
  useEffect(() => {
    if (!user || !firebaseUser || !user.userId) {
      setCurrentOrganization(null);
      setCurrentMembership(null);
      setOrganizations([]);
      setMemberships([]);
      setOrganizationFromDevice(false);
      setLoading(false);
      return;
    }

    // Guards against a late resolution from a superseded attempt overwriting
    // the state a newer one already set (sign-out, org switch, or a retry that
    // raced the attempt it replaced).
    let cancelled = false;

    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);

        const { userMemberships, orgs } = await loadUserOrganizations(user.userId, {
          onAttemptFailed: (err, attempt) =>
            console.warn(
              `[OrganizationContext] Membership load attempt ${attempt + 1} failed; retrying`,
              err,
            ),
        });
        if (cancelled) return;

        setMemberships(userMemberships);
        setOrganizations(orgs);

        const selection = selectOrganization(orgs, userMemberships, user.organizationId);
        if (selection.fellBackFromPreferred) {
          console.warn(
            `[OrganizationContext] Preferred org ${user.organizationId} not found in memberships, falling back to ${selection.organization?.organizationId}`,
          );
        }
        // A load that SUCCEEDED is authoritative, including when it resolves
        // nothing. Retaining the previous org on an empty result would keep a
        // revoked or offboarded user operating against an org they are no
        // longer a member of — and this selection now re-runs for the life of
        // the session (reconnect/foreground retries), so that would be a
        // standing condition rather than a first-mount edge case. Failures do
        // NOT reach here; they are caught below and leave state untouched.
        setCurrentOrganization(selection.organization);
        setCurrentMembership(selection.membership);
        setOrganizationFromDevice(false);
        // Offline cold start (plan step A): keep this device's copy current.
        // A no-op unless the app registered an offline identity store.
        if (selection.organization) {
          saveIdentity(user.userId, { organization: selection.organization, membership: selection.membership ?? null });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching organizations:', err);
        setError(err instanceof Error ? err : new Error('Failed to load organizations'));
        // OFFLINE COLD START (plan step A): no signal, and this user saved their
        // org on a previous online start — use it so the saved tickets render
        // instead of OrgBoundary's dead end. Connectivity failures only (never a
        // server refusal), only this user's own record, and only the org their
        // profile names. `error` stays set, so the reconnect/foreground retry
        // below still runs and replaces it with the live org.
        if (isConnectivityFailure(err)) {
          const saved = await loadSavedIdentity(user.userId);
          if (cancelled) return;
          const org = saved?.organization;
          if (org && (!user.organizationId || org.organizationId === user.organizationId)) {
            console.warn('[OrganizationContext] Offline — using the organization saved on this device.');
            setCurrentOrganization(org);
            setCurrentMembership(saved?.membership ?? null);
            setOrganizationFromDevice(true);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOrganizations();

    // Set up real-time listener for current organization
    let unsubscribe: (() => void) | undefined;

    if (user.organizationId) {
      // A failed listener is dead. Transient errors (deadline-exceeded / unavailable / aborted) re-subscribe after 2/8/30 s
      // (Sentry CLIENT-2F class); the cleanup below stops a pending retry, so a sign-out or org switch never leaves one behind.
      // Anything else, or three transient failures in a row, sets `error` exactly as before (the app keeps the last org).
      const orgId = user.organizationId;
      unsubscribe = subscribeWithTransientRetry<DocSnap>({
        label: 'organization',
        subscribe: (onData, onError) => onSnapshot(doc(db, COLLECTIONS.ORGANIZATIONS, orgId), onData, onError),
        onData: (snapshot) => {
          if (snapshot.exists()) {
            setCurrentOrganization(snapshot.data() as Organization);
            setError(null);
          }
        },
        onError: (err) => {
          // eslint-disable-next-line no-restricted-syntax -- Firestore snapshot listener, not a callable
          console.error('Error listening to organization changes:', err);
          setError(err instanceof Error ? err : new Error('Failed to load organization'));
        },
      });
    }

    return () => {
      cancelled = true;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, firebaseUser, user?.organizationId, reloadNonce]);

  // Recover from a failed load without the user having to restart the app.
  //
  // Only armed while `error` is set, so a healthy session adds no listeners and
  // does no extra reads. `online` covers walking out of a dead zone; the
  // visibility check covers an installed PWA resumed from the background, which
  // fires no navigation and would otherwise never re-ask.
  useEffect(() => {
    if (!error) return;

    const onOnline = () => retryLoad();
    const onVisible = () => {
      if (document.visibilityState === 'visible') retryLoad();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [error, retryLoad]);

  // Switch to a different organization
  const switchOrganization = useCallback(async (organizationId: string) => {
    try {
      const org = organizations.find((o) => o.organizationId === organizationId);
      const membership = memberships.find((m) => m.organizationId === organizationId);

      if (!org || !membership) {
        throw new Error('Organization not found or you are not a member');
      }

      setCurrentOrganization(org);
      setCurrentMembership(membership);

      // Optionally update user's preferred organization in Firestore
      // This would require importing updateUserProfileData from firebase-helpers
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  }, [organizations, memberships]);

  // Refresh organizations list
  const refreshOrganizations = useCallback(async () => {
    if (!user?.userId) return;

    try {
      setError(null);
      const { userMemberships, orgs } = await loadUserOrganizations(user.userId);
      setMemberships(userMemberships);
      setOrganizations(orgs);

      // Re-select, don't just repopulate. This previously refreshed the org
      // LIST and left `currentOrganization` untouched — so calling it to
      // recover from a failed initial load restored everything except the one
      // value every org-scoped query is gated on, and the app stayed blank.
      // Applied unconditionally for the same reason as the initial load: a
      // successful refresh that finds no memberships is an answer.
      const selection = selectOrganization(orgs, userMemberships, user.organizationId);
      setCurrentOrganization(selection.organization);
      setCurrentMembership(selection.membership);
    } catch (err) {
      console.error('Error refreshing organizations:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh organizations'));
    }
  }, [user]);

  // Check if user has a specific role in current organization
  const hasOrgRole = useCallback((role: MemberRole): boolean => {
    return currentMembership?.role === role;
  }, [currentMembership]);

  // Check if user is organization owner
  const isOrgOwner = useCallback((): boolean => {
    return currentMembership?.role === 'Owner';
  }, [currentMembership]);

  // Check if user is organization admin
  const isOrgAdmin = useCallback((): boolean => {
    return currentMembership?.role === 'Admin' || currentMembership?.role === 'Owner';
  }, [currentMembership]);

  // Check if user can manage members
  const canManageMembers = useCallback((): boolean => {
    return (
      currentMembership?.role === 'Owner' ||
      currentMembership?.role === 'Admin'
    );
  }, [currentMembership]);

  const value = useMemo<OrganizationContextType>(() => ({
    currentOrganization,
    currentMembership,
    organizations,
    memberships,
    loading,
    error,
    organizationFromDevice,
    switchOrganization,
    refreshOrganizations,
    retryLoad,
    hasOrgRole,
    isOrgOwner,
    isOrgAdmin,
    canManageMembers,
  }), [
    currentOrganization,
    currentMembership,
    organizations,
    memberships,
    loading,
    error,
    organizationFromDevice,
    switchOrganization,
    refreshOrganizations,
    retryLoad,
    hasOrgRole,
    isOrgOwner,
    isOrgAdmin,
    canManageMembers,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

const refuseOrgInEmbed = (): Promise<never> =>
  Promise.reject(new Error('Organizations are not available inside an embedded form'));

const ANONYMOUS_ORGANIZATION: OrganizationContextType = {
  currentOrganization: null,
  currentMembership: null,
  organizations: [],
  memberships: [],
  loading: false,
  error: null,
  organizationFromDevice: false,
  switchOrganization: refuseOrgInEmbed,
  refreshOrganizations: refuseOrgInEmbed,
  retryLoad: () => {},
  hasOrgRole: () => false,
  isOrgOwner: () => false,
  isOrgAdmin: () => false,
  canManageMembers: () => false,
};

/**
 * Supplies a no-organization context without mounting OrganizationProvider (no
 * membership reads). Used ONLY by lib/embed/EmbedOnlyApp.tsx (PR #5628). With no
 * currentOrganization, public-form data sources take the public path keyed by the
 * form's own organizationId (components/forms/public/dataSource/useDataSource.ts).
 */
export function AnonymousOrganizationProvider({ children }: { children: ReactNode }) {
  return <OrganizationContext.Provider value={ANONYMOUS_ORGANIZATION}>{children}</OrganizationContext.Provider>;
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
